// src/components/ToastHost.jsx
import React, { useEffect, useRef, useState } from 'react';
import './ToastHost.css';

let nextId = 1;

const ToastHost = () => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  useEffect(() => {
    const onShow = (e) => {
      const detail = e?.detail || {};
      const id = detail.id || `t_${Date.now()}_${nextId++}`;
      const type = detail.type || 'info';
      const msg = detail.message || '';
      const duration = Number(detail.duration ?? 3500);
      const slideFrom = detail.slideFrom || 'top';
      const slow = !!detail.slow;
      const align = detail.align || 'center'; // 'left' | 'center'
      const pill = !!detail.pill;
      const variant = detail.variant || '';
      const delayMs = Number(detail.delayMs || 0);
      const addNow = () => {
        setToasts((prev) => {
          const t = { id, type, message: msg, hiding: false, slideFrom, slow, align, pill, variant };
          const idx = prev.findIndex((x) => x.id === id);
          // If a toast with this id already exists, update it in-place instead of duplicating
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = { ...next[idx], ...t };
            return next;
          }
          return [...prev, t];
        });
        // Reset timer for this id
        const existing = timersRef.current.get(id);
        if (existing) clearTimeout(existing);
        if (duration > 0) {
          const timer = setTimeout(() => hideToast(id), duration);
          timersRef.current.set(id, timer);
        } else {
          timersRef.current.delete(id);
        }
      };
      if (delayMs > 0) setTimeout(addNow, delayMs); else addNow();
    };

    const onHide = (e) => {
      const { id } = e?.detail || {};
      if (!id) return;
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, hiding: true } : t)));
      const timer = timersRef.current.get(id);
      if (timer) clearTimeout(timer);
      timersRef.current.delete(id);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 220);
    };

    const onUpdate = (e) => {
      const { id, message, type, align, pill, variant } = e?.detail || {};
      if (!id) return;
      setToasts((prev) => prev.map((t) => (
        t.id === id ? {
          ...t,
          message: message ?? t.message,
          type: type || t.type,
          align: align || t.align,
          pill: (typeof pill === 'boolean' ? pill : t.pill),
          variant: variant || t.variant,
        } : t
      )));
    };

    const onClear = () => {
      // Hide all toasts quickly
      setToasts((prev) => prev.map((t) => ({ ...t, hiding: true })));
      // Full remove after animation
      setTimeout(() => setToasts([]), 240);
      for (const t of timersRef.current.values()) clearTimeout(t);
      timersRef.current.clear();
    };

    window.addEventListener('toast:show', onShow);
    window.addEventListener('toast:hide', onHide);
    window.addEventListener('toast:update', onUpdate);
    window.addEventListener('toast:clear', onClear);
    return () => {
      window.removeEventListener('toast:show', onShow);
      window.removeEventListener('toast:hide', onHide);
      window.removeEventListener('toast:update', onUpdate);
      window.removeEventListener('toast:clear', onClear);
      for (const t of timersRef.current.values()) clearTimeout(t);
      timersRef.current.clear();
    };
  }, []);

  const hideToast = (id) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, hiding: true } : t)));
    const timer = timersRef.current.get(id);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(id);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 220);
  };

  return (
    <div className="toast-host" aria-live="polite" aria-atomic="true">
      {toasts.map((t) => {
        const hideClass = t.hiding ? (t.slideFrom === 'right' ? 'toast-hide-right' : 'toast-hide') : '';
        const slideClass = t.slideFrom === 'right' ? 'from-right' : '';
        const speedClass = t.slow ? 'slow' : '';
        const alignClass = t.align === 'left' ? 'align-left' : '';
        const pillClass = t.pill ? 'pill' : '';
        const variantClass = t.variant ? t.variant : '';
        return (
          <div key={t.id} className={`toast-card ${t.type || 'info'} ${slideClass} ${speedClass} ${alignClass} ${pillClass} ${variantClass} ${hideClass}`}>
            <div className="toast-message">{t.message}</div>
            <button className="toast-close" aria-label="Dismiss" onClick={() => hideToast(t.id)}>✕</button>
          </div>
        );
      })}
    </div>
  );
};

export default ToastHost;
